import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { ClientWindowSticker } from 'src/app/model/product/windowsticker/client-windowsticker';
import { WindowStickerSizeSelectionListControllerRequestService } from './service/window-sticker-size-selection-list-controller.service';
import { WindowStickerSizeMaterialsForFormatControllerRequestService } from './service/window-sticker-size-materials-for-format-controller-request.service';
import { WindowStickerSizeSingleSizeControllerRequestService } from './service/window-sticker-size-single-size-controller-request.service';
import { WindowStickerSizeGetter } from './helper/window-sticker-size-getter';
import { ClientWindowStickerFormHelper } from './helper/client-window-sticker-form-helper';
import {
  FoilChoices,
  LaminateChoices,
} from 'src/app/model/product/windowsticker/windowsticker';
import Material from 'src/app/model/material/material';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import {
  ClientStickerPart,
  ClientStickerPartInstance,
  CLIENT_STICKER_PART_TYPE,
} from 'src/app/model/product/windowsticker/client-sticker-part';
import { cloneDeep } from 'lodash';
import { EMPTY_OBJECT } from 'src/globals/globals';
import { ModalService } from 'src/app/services/modal.service';
import { isStandardStickerPart } from 'src/app/model/product/windowsticker/standard-sticker-part';
import StickerPart, {
  isStickerPart,
} from 'src/app/model/product/windowsticker/sticker-part';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';

@Component({
  selector: 'client-window-sticker-form',
  templateUrl: './client-window-sticker-form.component.html',
  styleUrls: ['./client-window-sticker-form.component.scss'],
})
export class ClientWindowStickerFormComponent
  extends SubscriptionBasedComponent
  implements OnInit, OnDestroy
{
  @Input()
  windowSticker: ClientWindowSticker;

  windowStickerSizeGetter: WindowStickerSizeGetter;

  temporaryMaterialOptions =
    ClientWindowStickerFormHelper.ALL_TEMPORARY_MATERIALS;
  permanentMaterialOptions =
    ClientWindowStickerFormHelper.ALL_PERMANENT_MATERIALS;
  temporaryLaminateOptions =
    ClientWindowStickerFormHelper.ALL_TEMPORARY_LAMINATES;
  permanentLaminateOptions =
    ClientWindowStickerFormHelper.ALL_PERMANENT_LAMINATES;

  disableAddSticker: boolean = true;
  enableDeleteStickerPart: boolean = false;

  constructor(
    windowStickerSizeSelectionListControllerRequestService: WindowStickerSizeSelectionListControllerRequestService,
    windowStickerSizeMaterialsForFormatControllerRequestService: WindowStickerSizeMaterialsForFormatControllerRequestService,
    windowStickerSizeSingleSizeControllerRequestService: WindowStickerSizeSingleSizeControllerRequestService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private modalService: ModalService
  ) {
    super();
    this.windowStickerSizeGetter = new WindowStickerSizeGetter(
      windowStickerSizeSelectionListControllerRequestService,
      windowStickerSizeMaterialsForFormatControllerRequestService,
      windowStickerSizeSingleSizeControllerRequestService
    );
  }

  ngOnInit(): void {
    this.windowStickerSizeGetter.fetchSelectionList();
    this.initializeSubscriptions();
  }

  private initializeSubscriptions(): void {
    this.addSubscription(
      this.windowStickerSizeGetter.getFormatsChangedSubject(),
      () => {
        this.handleGetValidMaterials();
      }
    );
  }

  ngOnDestroy(): void {
    if (this.windowStickerSizeGetter != null) {
      this.windowStickerSizeGetter.destroy();
    }
  }

  private async handleGetValidMaterials(): Promise<void> {
    const heightWidthTuples =
      ClientWindowStickerFormHelper.getHeightTuplesForMaterialsRequest(
        this.windowSticker
      );
    if (!CollectionHelper.isPopulated(heightWidthTuples)) {
      this.temporaryMaterialOptions =
        ClientWindowStickerFormHelper.ALL_TEMPORARY_MATERIALS;
      this.permanentMaterialOptions =
        ClientWindowStickerFormHelper.ALL_PERMANENT_MATERIALS;
    } else {
      const validMaterials =
        await this.windowStickerSizeGetter.fetchMaterialsForFormats(
          heightWidthTuples
        );
      this.temporaryMaterialOptions =
        validMaterials == null || validMaterials.temporaryMaterials == null
          ? []
          : validMaterials.temporaryMaterials;
      this.permanentMaterialOptions =
        validMaterials == null || validMaterials.permanentMaterials == null
          ? []
          : validMaterials.permanentMaterials;
      this.handlePreviouslySelectedMaterial();
    }
  }

  private handlePreviouslySelectedMaterial(): void {
    if (
      this.windowSticker.foil == null ||
      this.windowSticker.temporaryMaterial == null
    ) {
      return;
    }
    const matchingValidMaterial = this.windowSticker.temporaryMaterial
      ? this.temporaryMaterialOptions.find(
          (m) => m.material.ordinal === this.windowSticker.foil.ordinal
        )
      : this.permanentMaterialOptions.find(
          (m) => m.material.ordinal === this.windowSticker.foil.ordinal
        );
    if (matchingValidMaterial == null) {
      if (
        this.windowSticker.temporaryMaterial &&
        CollectionHelper.isPopulated(this.permanentMaterialOptions)
      ) {
        this.windowSticker.temporaryMaterial = false;
      } else if (
        !this.windowSticker.temporaryMaterial &&
        CollectionHelper.isPopulated(this.temporaryMaterialOptions)
      ) {
        this.windowSticker.temporaryMaterial = true;
      }
    }
  }

  temporaryMaterialChanged(): void {
    if (this.windowSticker.temporaryMaterial === true) {
      this.trySelectTemporaryDefaults();
    } else {
      const previousFoil = this.windowSticker.foil;
      this.windowSticker.foil = null;
      this.windowSticker.laminate = null;
      if (previousFoil != null) {
        this.foilChanged();
      }
    }
  }

  private trySelectTemporaryDefaults(): void {
    const previousFoil = this.windowSticker.foil;
    const monomer =
      this.temporaryMaterialOptions == null
        ? null
        : this.temporaryMaterialOptions.find(
            (o) =>
              o.material != null &&
              o.material.ordinal === FoilChoices.MONOMER.ordinal
          );

    this.windowSticker.foil = monomer == null ? null : monomer.material;
    this.windowSticker.laminate = LaminateChoices.TEMPORARY_GLOSSY;

    if (!this.compareMaterialOptions(previousFoil, this.windowSticker.foil)) {
      this.foilChanged();
    }
  }

  foilChanged(): void {
    this.windowStickerSizeGetter.getMaterialChangedSubject().next();
  }

  handleTypeIsSelected(part: ClientStickerPart): void {
    this.calculatePartType(part);
    this.calculateDisableAddSticker();
    this.handleGetValidMaterials();
    this.projectDisplayObservableService
      .getProductPartTypeChangedSubscribable()
      .next();
  }

  private calculateDisableAddSticker(): void {
    this.disableAddSticker =
      CollectionHelper.isPopulated(this.windowSticker.clientStickerParts) &&
      this.windowSticker.clientStickerParts.some((p) => p.instance == null);
  }

  calculateEnableDeleteStickerPart(): void {
    this.enableDeleteStickerPart =
      this.windowSticker.clientStickerParts != null &&
      this.windowSticker.clientStickerParts.length > 1;
  }

  addStickerPart(): void {
    const clientStickerPart = new ClientStickerPart();
    clientStickerPart.instance = CollectionHelper.isPopulated(
      this.windowSticker.clientStickerParts
    )
      ? this.createNewStickerPartInstance(
          this.windowSticker.clientStickerParts[
            this.windowSticker.clientStickerParts.length - 1
          ].instance
        )
      : EMPTY_OBJECT;
    this.windowSticker.clientStickerParts.push(clientStickerPart);
    this.calculatePartType(clientStickerPart);
    this.calculateDisableAddSticker();
    this.calculateEnableDeleteStickerPart();
    this.projectDisplayObservableService
      .getProductPartTypeChangedSubscribable()
      .next();
  }

  private calculatePartType(clientStickerPart: ClientStickerPart): void {
    if (clientStickerPart == null) {
      return;
    }
    if (clientStickerPart.instance == null) {
      clientStickerPart.partType = null;
    } else if (isStandardStickerPart(clientStickerPart.instance)) {
      clientStickerPart.partType =
        CLIENT_STICKER_PART_TYPE.STANDARD_STICKER_PART;
    } else if (isStickerPart(clientStickerPart.instance)) {
      clientStickerPart.partType = CLIENT_STICKER_PART_TYPE.STICKER_PART;
    }
  }

  private createNewStickerPartInstance(
    sourceStickerPartInstance: ClientStickerPartInstance
  ): ClientStickerPartInstance {
    if (isStandardStickerPart(sourceStickerPartInstance)) {
      return cloneDeep(sourceStickerPartInstance);
    } else if (isStickerPart(sourceStickerPartInstance)) {
      return new StickerPart();
    } else {
      return EMPTY_OBJECT;
    }
  }

  deleteStickerPart(index: number): void {
    this.modalService.confirmDialog(
      'Deelsticker verwijderen',
      'Bent u zeker dat u deze deelsticker wilt verwijderen?',
      (b) => {
        if (b) {
          const part = this.windowSticker.clientStickerParts.splice(
            index,
            1
          )[0];
          this.calculateDisableAddSticker();
          this.calculateEnableDeleteStickerPart();
          if (isStandardStickerPart(part.instance)) {
            this.handleGetValidMaterials();
          }
          this.projectDisplayObservableService
            .getProductPartTypeChangedSubscribable()
            .next();
        }
      }
    );
  }

  compareMaterialOptions(o1: Material, o2: Material): boolean {
    if (o1 == null && o2 == null) {
      return true;
    }
    if (o1 == null || o2 == null) {
      return false;
    }
    return o1.ordinal === o2.ordinal;
  }
}
